
import React from "react";
import { Button } from "@/components/ui/button";
import { Check, Clock, ChevronLeft, Play } from "lucide-react";
import { Logo } from "@/components/ui/logo";

interface Lesson {
  id: number;
  title: string;
  duration: string;
  completed: boolean;
  videoUrl: string;
  thumbnail: string;
}

interface Course {
  id: number;
  title: string;
  progress: number;
  image: string;
  instructor: string;
  instructorImage: string;
  description: string;
  lessons: Lesson[];
}

interface TutorialCourseDetailProps {
  course: Course | null;
  onSelectLesson: (lessonId: number) => void;
  onBack: () => void;
}

export const TutorialCourseDetail: React.FC<TutorialCourseDetailProps> = ({
  course,
  onSelectLesson,
  onBack
}) => {
  if (!course) return null;

  const createPlaceholderImage = (title: string) => {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-black/50 p-4 text-center">
        <div className="mb-4">
          <Logo variant="icon-only" size="lg" />
        </div>
        <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
        <p className="text-gray-300 text-sm">Hoss Immo Consulting Tutorial</p>
      </div>
    );
  };

  const completedLessons = course.lessons.filter(lesson => lesson.completed).length;
  
  return (
    <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <div className="glass-morphism rounded-xl p-6 mb-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-2xl font-bold mb-2">{course.title}</h1>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="h-1.5 bg-gray-700 rounded-full w-48">
                    <div 
                      className="h-full bg-[#41c7f0] rounded-full" 
                      style={{ width: `${course.progress}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-white">{course.progress}%</span>
                </div>
                <div className="text-sm text-gray-400">
                  {completedLessons} von {course.lessons.length} Lektionen abgeschlossen
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={onBack}>
              <ChevronLeft size={16} className="mr-1" /> Zurück zur Übersicht
            </Button>
          </div>
          
          <h2 className="text-lg font-medium mb-4">Kursinhalte</h2>
          <ul className="divide-y divide-gray-800/30">
            {course.lessons.map((lesson) => (
              <li key={lesson.id} className="py-4">
                <div className="flex items-start gap-4">
                  <div 
                    className="relative w-32 h-20 rounded-md overflow-hidden cursor-pointer flex-shrink-0"
                    onClick={() => onSelectLesson(lesson.id)}
                  >
                    <div className="absolute inset-0 bg-black/30">
                      {createPlaceholderImage(lesson.title)}
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <h3 
                        className="font-medium cursor-pointer hover:text-[#41c7f0] transition-colors"
                        onClick={() => onSelectLesson(lesson.id)}
                      >
                        {lesson.title}
                      </h3>
                      <div className="flex items-center text-sm text-gray-400">
                        <Clock size={14} className="mr-1" />
                        {lesson.duration}
                      </div>
                    </div>
                    
                    <div className="mt-2 flex justify-between">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => onSelectLesson(lesson.id)}
                      >
                        {lesson.completed ? 'Wiederholen' : 'Starten'}
                      </Button>
                      
                      {lesson.completed && (
                        <div className="flex items-center text-[#41c7f0] text-sm">
                          <Check size={14} className="mr-1" />
                          Angesehen
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
      
      <div className="lg:col-span-1">
        <div className="glass-morphism rounded-xl p-6 mb-6">
          <h2 className="text-lg font-medium mb-4">Über diesen Kurs</h2>
          <div className="mb-6 text-gray-300 text-sm">
            {course.description}
          </div>
          
          <h3 className="text-md font-medium mb-3">Kursleiter</h3>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full overflow-hidden">
              <img 
                src={course.instructorImage} 
                alt={course.instructor} 
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="font-medium">{course.instructor}</p>
              <p className="text-sm text-gray-400">Kursleiter</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
